import { useMemo } from "react";
import { useNavigate } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/shared/empty-state";
import { CardSkeleton } from "@/components/shared/loading-skeleton";
import { ROUTES } from "@/app/routes";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { usePluginManifestsQuery } from "@/lib/query/plugin-state";
import type { PluginControlPlaneManifestRecord } from "../../../../../packages/shared-types/src/plugin-control-plane.js";

export function PluginsPage() {
  const { tenantId: activeTenantId } = useTenantContext();
  const navigate = useNavigate();
  useDocumentTitle("Plugins");

  const { data: manifests = [], isLoading } = usePluginManifestsQuery();
  const sorted = useMemo(
    () => [...manifests].toSorted((left, right) => left.pluginId.localeCompare(right.pluginId)),
    [manifests],
  );

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        <CardSkeleton />
        <CardSkeleton />
      </div>
    );
  }

  if (sorted.length === 0) {
    return (
      <EmptyState
        title="No plugins available"
        description="This control plane is not exposing any plugin manifests."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="gap-2">
          <CardTitle>Plugins</CardTitle>
          <CardDescription>
            Plugins are runtime capabilities. Configure them in the control plane and the managed runtime
            hydrates them automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2 text-xs" style={{ color: "var(--muted-foreground)" }}>
          <Badge variant="outline">{sorted.length} plugins</Badge>
          <Badge variant="outline">
            Tenant: {activeTenantId ?? "\u2014"}
          </Badge>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {sorted.map((manifest) => (
          <PluginCard
            key={manifest.pluginId}
            manifest={manifest}
            onOpen={() => {
              if (!activeTenantId) {
                return;
              }
              navigate(ROUTES.tenantPlugin(activeTenantId, manifest.pluginId));
            }}
          />
        ))}
      </div>
    </div>
  );
}

function PluginCard(params: {
  manifest: PluginControlPlaneManifestRecord;
  onOpen: () => void;
}) {
  const { manifest } = params;
  const providerCount = manifest.providers?.length ?? 0;
  const channelCount = manifest.channels?.length ?? 0;
  const credentialCount = manifest.credentialKeys?.length ?? 0;

  return (
    <Card className="h-full">
      <CardHeader className="gap-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <CardTitle className="text-base">{manifest.name ?? manifest.pluginId}</CardTitle>
            <div className="text-xs font-mono" style={{ color: "var(--muted-foreground)" }}>
              {manifest.pluginId}
            </div>
          </div>
          <Badge variant="outline">plugin</Badge>
        </div>
        {manifest.description ? (
          <CardDescription>{manifest.description}</CardDescription>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2 text-xs" style={{ color: "var(--muted-foreground)" }}>
          <Badge variant="outline">{providerCount} providers</Badge>
          <Badge variant="outline">{channelCount} channels</Badge>
          <Badge variant="outline">{credentialCount} credential keys</Badge>
        </div>
        <Button size="sm" onClick={params.onOpen}>
          Configure
        </Button>
      </CardContent>
    </Card>
  );
}

